import {
    useRef,
    useState
} from 'react';

import {Alert, Anchor, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {useVerifiedPermissions} from '@flexinet/auth-components';

import {productFeaturesQueryKeys, useProductFeatures} from '../hooks/useProductFeatures.ts';
import {useProductFeaturesFilter} from '../hooks/useProductFeaturesFilter.ts';
import {useProductFeaturesSelection} from '../hooks/useProductFeaturesSelection.ts';
import {useProductFeaturesTableColumns} from '../hooks/useProductFeaturesTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {update} from '../services/ProductFeaturesService.ts';
import {rbacActions} from '../../../config/rbac/actions.ts';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';
import {ProductFeature} from '../types';
import {useQueryClient} from '@tanstack/react-query';

export const RecordsTable = () => {
    const {
        selectedRecords,
        setSelectedRecords
    } = useProductFeaturesSelection();
    const {columns} = useProductFeaturesTableColumns();
    const {activeCount, handleClearFilters} = useProductFeaturesFilter();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<ProductFeature | null>(null);
    const queryClient = useQueryClient();

    // Editing is a write, so the row click that opens the edit drawer is gated on
    // UpdateProductFeature, as Add and Delete are on theirs.
    const {allowedActions} = useVerifiedPermissions();
    const canEdit = allowedActions.includes(rbacActions.UpdateProductFeature);

    const {
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useProductFeatures();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const term = keyword.trim();

    // Resolves true when saved; the drawer closes only then (FormDrawer). The input is
    // whitelisted by the service (buildUpdateProductFeatureInput).
    const handleDrawerSubmit = async (values: ProductFeature): Promise<boolean> => {
        if (!selectedRecord?.id) {
            return false;
        }
        try {
            await update(selectedRecord.id, values);
            queryClient.invalidateQueries({queryKey: productFeaturesQueryKeys.all});
            notifySuccess(I18n.get('Product Feature updated'));
            return true;
        } catch (e) {
            notifyError(e);
            return false;
        }
    };

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load product features')}>
                    {I18n.get('The billing API did not return the product features list. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            <MantineDataTable<ProductFeature>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope as
                // the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={canEdit ? ({record}) => {
                    setSelectedRecord(record);
                    setDrawerOpened(true);
                } : undefined}
            />

            {/*
              THREE EMPTY-ISH STATES (§13). A search with no match, a filter with no match
              (offers Clear filters: the list opens filtered to Active, so an empty list here
              is usually the filter, not missing data), and genuinely nothing yet. Gated on
              !loading so it does not flash during the first fetch, and on !isError so a
              failed load keeps its own Alert rather than claiming there are none.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                term ? (
                    <Alert color='gray' title={I18n.get('No matching product features')}>
                        {`${I18n.get('Nothing in the loaded product features matches')} "${term}". ${I18n.get('Clear the search to see every loaded row. A match may still be on a page that has not been loaded yet.')}`}
                    </Alert>
                ) : activeCount > 0 ? (
                    <Alert color='gray' title={I18n.get('No product features match the filter')}>
                        {I18n.get('No product feature matches the current filter.')}{' '}
                        <Anchor component='button' type='button' onClick={handleClearFilters}>
                            {I18n.get('Clear filters')}
                        </Anchor>
                    </Alert>
                ) : (
                    <Alert color='blue' title={I18n.get('No product features yet')}>
                        {I18n.get('A product feature is a capability a product can include. Use Add Product Feature to create the first one.')}
                    </Alert>
                )
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'product feature'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
